import { FormEvent, useEffect, useMemo, useState } from "react";
import type { Bag } from "../lib/types";
import { ModalCloseButton } from "./ModalCloseButton";

export interface MoveApparatusOption {
  apparatusId: string;
  apparatusName: string;
  bags: Bag[];
}

export interface MoveStockValue {
  enteredName: string;
  quantity: number;
  apparatusId: string;
  bagId: string;
}

export function MoveStockSheet({
  open,
  title,
  maxQuantity,
  initialName,
  apparatusOptions,
  onClose,
  onMove
}: {
  open: boolean;
  title: string;
  maxQuantity: number;
  initialName: string;
  apparatusOptions: MoveApparatusOption[];
  onClose: () => void;
  onMove: (value: MoveStockValue) => Promise<void>;
}) {
  const firstOption = useMemo(
    () => apparatusOptions.find((option) => option.bags.length > 0) ?? apparatusOptions[0] ?? null,
    [apparatusOptions]
  );
  const availableQuantity = Math.max(1, maxQuantity);

  const [value, setValue] = useState<MoveStockValue>({
    enteredName: initialName,
    quantity: availableQuantity,
    apparatusId: firstOption?.apparatusId ?? "",
    bagId: firstOption?.bags[0]?.id ?? ""
  });
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    setValue({
      enteredName: initialName,
      quantity: availableQuantity,
      apparatusId: firstOption?.apparatusId ?? "",
      bagId: firstOption?.bags[0]?.id ?? ""
    });
    setFormError("");
  }, [initialName, availableQuantity, firstOption, open]);

  const selectedBags = useMemo(
    () => apparatusOptions.find((option) => option.apparatusId === value.apparatusId)?.bags ?? [],
    [apparatusOptions, value.apparatusId]
  );

  if (!open) return null;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setFormError("");

    if (!value.enteredName.trim()) {
      setFormError("Your Name is required.");
      return;
    }

    if (!value.bagId) {
      setFormError("Select a destination bag.");
      return;
    }

    if (value.quantity <= 0) {
      setFormError("Quantity must be at least 1.");
      return;
    }

    setBusy(true);
    try {
      await onMove(value);
      onClose();
    } catch (error: any) {
      setFormError(error?.message ?? "Unable to move stock.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={() => (busy ? undefined : onClose())}>
      <div className="modal-card move-sheet-card" onClick={(event) => event.stopPropagation()}>
        <ModalCloseButton onClick={onClose} disabled={busy} />
        <h3>{title}</h3>

        <form onSubmit={submit} className="stack-sm">
          <label className="stack-xs">
            <span className="label">Your Name</span>
            <input
              className="input"
              value={value.enteredName}
              onChange={(event) => setValue((prev) => ({ ...prev, enteredName: event.target.value }))}
              required
            />
          </label>

          <label className="stack-xs">
            <span className="label">Quantity</span>
            <input
              className="input"
              type="number"
              min={1}
              max={availableQuantity}
              value={value.quantity}
              onChange={(event) => {
                const next = Number(event.target.value);
                setValue((prev) => ({
                  ...prev,
                  quantity: Number.isFinite(next) ? Math.max(1, Math.min(availableQuantity, Math.round(next))) : availableQuantity
                }));
              }}
            />
          </label>

          <label className="stack-xs">
            <span className="label">Apparatus</span>
            <select
              className="input"
              value={value.apparatusId}
              onChange={(event) => {
                const apparatusId = event.target.value;
                const nextBags = apparatusOptions.find((option) => option.apparatusId === apparatusId)?.bags ?? [];
                setValue((prev) => ({
                  ...prev,
                  apparatusId,
                  bagId: nextBags[0]?.id ?? ""
                }));
              }}
              required
            >
              {apparatusOptions.map((option) => (
                <option key={option.apparatusId} value={option.apparatusId}>
                  {option.apparatusName}
                </option>
              ))}
            </select>
          </label>

          <label className="stack-xs">
            <span className="label">Bag</span>
            <select
              className="input"
              value={value.bagId}
              onChange={(event) => setValue((prev) => ({ ...prev, bagId: event.target.value }))}
              required
            >
              {selectedBags.map((bag) => (
                <option key={bag.id} value={bag.id}>
                  {bag.name}
                </option>
              ))}
            </select>
          </label>

          {formError ? <p className="error-text small">{formError}</p> : null}

          <div className="actions-wrap">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={busy}>
              {busy ? "Moving..." : "Move"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
